// ============================================================
// CLUBHUB – Kalendertage ohne Zeitzonenfallen
// ============================================================
// Reine Daten („2026-10-23“) vergleichen wir als TEXT und rechnen
// sie ueber UTC in Tage um — `new Date("2026-10-23")` waere UTC-
// Mitternacht und liefe in Zeitzonen westlich von Greenwich auf den
// Vortag. Zeitpunkte ohne „Z“ („2026-10-23T19:30:00“) liest jeder
// Rechner als seine Ortszeit; Server und Browser zeigen damit
// dieselbe Uhrzeit. Deshalb hier nie `toISOString()`.
// Browsertauglich, auch von der Pinnwand benutzt.
// ============================================================

const zwei = (n: number) => String(n).padStart(2, "0");

/** Heute als `JJJJ-MM-TT` in Ortszeit. */
export function isoHeute(d: Date = new Date()): string {
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;
}

/** Jetzt als `JJJJ-MM-TTTHH:MM:SS` in Ortszeit (ohne Zone). */
export function isoJetzt(d: Date = new Date()): string {
  return `${isoHeute(d)}T${zwei(d.getHours())}:${zwei(d.getMinutes())}:${zwei(d.getSeconds())}`;
}

/** Laufende Tagesnummer eines Datums — fuer Differenzen in Tagen. */
export function tagNummer(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return Math.round(Date.UTC(y!, (m ?? 1) - 1, d ?? 1) / 86_400_000);
}

/** Wie viele Kalendertage von `von` bis `bis` (negativ: zurueck). */
export function tageZwischen(von: string, bis: string): number {
  return tagNummer(bis) - tagNummer(von);
}

/** `iso` um `tage` verschoben, als `JJJJ-MM-TT`. */
export function plusTage(iso: string, tage: number): string {
  const d = new Date((tagNummer(iso) + tage) * 86_400_000);
  return `${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}-${zwei(d.getUTCDate())}`;
}

/** Der Erste des Folgemonats. */
export function ersterNaechsterMonat(iso: string): string {
  const [y, m] = iso.split("-").map(Number);
  return m === 12 ? `${y! + 1}-01-01` : `${y}-${zwei(m! + 1)}-01`;
}

/** Der naechste `wochentag` (0 = Sonntag) ab `iso` (einschliesslich). */
export function naechsterWochentag(iso: string, wochentag: number): string {
  const heute = new Date(tagNummer(iso) * 86_400_000).getUTCDay();
  return plusTage(iso, (wochentag - heute + 7) % 7);
}

/** Ist das ein gueltiges Datum `JJJJ-MM-TT`? */
export function istIsoDatum(wert: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(wert)) return false;
  const [y, m, d] = wert.split("-").map(Number);
  const probe = new Date(Date.UTC(y!, m! - 1, d!));
  return probe.getUTCMonth() === m! - 1 && probe.getUTCDate() === d;
}

const KURZ = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", timeZone: "UTC" });
const WOCHENTAG = new Intl.DateTimeFormat("de-DE", { weekday: "short", timeZone: "UTC" });

/** „23.10.“ — fuer Daten im laufenden Jahr. */
export function datumKurz(iso: string): string {
  return KURZ.format(new Date(tagNummer(iso) * 86_400_000));
}

const LANG = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });

/** „Freitag, 14. November“ */
export function datumLang(iso: string): string {
  return LANG.format(new Date(tagNummer(iso) * 86_400_000));
}

/** „Di“ */
export function wochentag(iso: string): string {
  return WOCHENTAG.format(new Date(tagNummer(iso) * 86_400_000)).replace(".", "");
}

/** „heute“, „gestern“, „vor 3 Tagen“, sonst „am 12.09.2026“ — relativ zu `heute`. */
export function tageRelativ(iso: string, heute: string): string {
  const t = tageZwischen(iso.slice(0, 10), heute);
  if (t === 0) return "heute";
  if (t === 1) return "gestern";
  if (t > 1 && t < 7) return `vor ${t} Tagen`;
  if (t === -1) return "morgen";
  if (t < -1 && t > -7) return `in ${-t} Tagen`;
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `am ${d}.${m}.${y}`;
}

/** Uhrzeit „19:30“ aus einem Zeitpunkt ohne Zone. */
export function uhrzeit(iso: string): string {
  return iso.slice(11, 16);
}
